import {
    REQUIRED_UPDATE_FIELDS,
    OPTIONAL_UPDATE_FIELDS,
    ID,
    NUMERIC_FIELDS,
    BOOLEAN_FIELDS,
    ARRAY_FIELDS,
} from '../../constants/csvFields';
import { formatToNumber, isBoolean, isIdField, isValidDate } from 'pages/Sites/SiteImports/helpers/utils';
import { getErrorAsString } from 'helpers/getErrorAsString';

type CsvDataRow = Array<string>;
type CsvCheckReturn = {
    success: boolean;
    error?: string;
};
const WEEKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'] as const;
const WEEK_TYPES = ['evenWeek', 'oddWeek'] as const;
const VALID_SITE_TYPES = ['BrokenProductsWarehouse', 'ExpiredProductsWarehouse', 'Microstore', 'Warehouse'];
const TIME_WINDOW_REGEX = /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/;
const INTEGER_FIELDS = [
    'accessTime',
    'estimatedUserCount',
    'unknownShrinkageBillingThreshold',
    'reassortmentStockTargets.dessert',
    'reassortmentStockTargets.main',
    'reassortmentStockTargets.smallBites',
    'reassortmentStockTargets.starter',
    'reassortmentOnboardingStockTargets.dessert',
    'reassortmentOnboardingStockTargets.main',
    'reassortmentOnboardingStockTargets.smallBites',
    'reassortmentOnboardingStockTargets.starter',
    'reassortmentStockTargetRatios.monday',
    'reassortmentStockTargetRatios.tuesday',
    'reassortmentStockTargetRatios.wednesday',
    'reassortmentStockTargetRatios.thursday',
    'reassortmentStockTargetRatios.friday',
];

export const checkSiteCsvImport = (csvData: string[][]): CsvCheckReturn => {
    try {
        // Basic CSV structure validation
        if (!csvData.length) {
            throw Error('Le fichier CSV est vide');
        }

        const [initialHeader, ...rawData] = csvData;
        const data = rawData.filter((row) => {
            return row.some((cell) => cell.trim() !== '');
        });
        if (!initialHeader.length) {
            throw Error("Le fichier CSV ne contient pas d'en-tête");
        }

        if (!data.length) {
            throw Error('Le fichier CSV ne contient pas de données');
        }

        // Check row length consistency
        const arrayLength = initialHeader.length;
        data.forEach((siteArrayFromCSV: CsvDataRow, rowIndex: number): void => {
            if (siteArrayFromCSV.length !== arrayLength) {
                throw Error(
                    `Veuillez vérifier le fichier CSV. Mauvais nombre de colonnes sur la ligne ${rowIndex + 2}`,
                );
            }
        });

        // Validate headers and prepare normalized header array
        const siteFields = [...REQUIRED_UPDATE_FIELDS, ...OPTIONAL_UPDATE_FIELDS].map((field) =>
            field.name.toLowerCase(),
        );
        const nonValidFields: string[] = [];
        initialHeader.forEach((headerField, index) => {
            const fieldLower = headerField.toLowerCase().trim();
            const fieldIndex = siteFields.findIndex((elem) => elem === fieldLower);

            if (fieldIndex === -1) {
                if (isIdField(headerField)) {
                    initialHeader[index] = ID;
                    return;
                }
                nonValidFields.push(headerField);
            }
        });

        if (nonValidFields.length) {
            throw Error(`Veuillez vérifier le fichier CSV. Champs ${nonValidFields.join(', ')} non reconnus`);
        }

        const header = initialHeader.map((value) => value.toLowerCase().trim());

        // Check required ID field
        const hasIdField = header.some((field) => isIdField(field));
        if (!hasIdField) {
            throw Error('Le champ _id est requis dans le fichier CSV');
        }

        // Validate delivery days structure
        const daysOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        daysOfWeek.forEach((day) => {
            const fieldName = `deliverydays.${day}`;
            const fieldIndex = header.findIndex((h) => h === fieldName);
            if (fieldIndex !== -1) {
                data.forEach((row, rowIndex) => {
                    const value = row[fieldIndex];
                    if (value && !isBoolean(value)) {
                        throw Error(
                            `Veuillez vérifier le fichier CSV. Le champ deliveryDays.${day} doit être true/false sur la ligne ${rowIndex + 2}`,
                        );
                    }
                });
            }
        });

        // Validate freshReassortDays structure
        daysOfWeek.forEach((day) => {
            const fieldName = `freshreassortdays.${day}`;
            const fieldIndex = header.findIndex((h) => h === fieldName);
            if (fieldIndex !== -1) {
                data.forEach((row, rowIndex) => {
                    const value = row[fieldIndex];
                    if (value && !isBoolean(value)) {
                        throw Error(
                            `Veuillez vérifier le fichier CSV. Le champ freshReassortDays.${day} doit être true/false sur la ligne ${rowIndex + 2}`,
                        );
                    }
                });
            }
        });

        // Validate dryReassortDays structure
        daysOfWeek.forEach((day) => {
            const fieldName = `dryreassortdays.${day}`;
            const fieldIndex = header.findIndex((h) => h === fieldName);
            if (fieldIndex !== -1) {
                data.forEach((row, rowIndex) => {
                    const value = row[fieldIndex];
                    if (value && !isBoolean(value)) {
                        throw Error(
                            `Veuillez vérifier le fichier CSV. Le champ dryReassortDays.${day} doit être true/false sur la ligne ${rowIndex + 2}`,
                        );
                    }
                });
            }
        });

        // Validate reassortment stock target ratios
        const weekdays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
        weekdays.forEach((day) => {
            const fieldName = `reassortmentstocktargetratios.${day}`;
            const fieldIndex = header.findIndex((h) => h === fieldName);
            if (fieldIndex !== -1) {
                data.forEach((row, rowIndex) => {
                    const value = row[fieldIndex];
                    if (value) {
                        const numValue = formatToNumber(value);
                        if (Number.isNaN(numValue) || numValue < 0) {
                            throw Error(
                                `Veuillez vérifier le fichier CSV. Le champ reassortmentStockTargetRatios.${day} doit être un nombre positif sur la ligne ${rowIndex + 2}`,
                            );
                        }
                        if (!Number.isInteger(numValue)) {
                            throw Error(
                                `Veuillez vérifier le fichier CSV. Le champ reassortmentStockTargetRatios.${day} doit être un nombre entier sur la ligne ${rowIndex + 2}`,
                            );
                        }
                    }
                });
            }
        });

        // Validate urbantz metadata structure
        const urbantzServices = [
            'needsInventoryBoissons',
            'needsInventorySnack',
            'needsInventoryNonAlimentaire',
            'needsCleaningMachineNECTA',
            'needsSmallExpiryDateInventory',
            'replaceCompleteInventoryByZeroInventory',
            'needsPunctualExpiryDateInventory',
        ];

        urbantzServices.forEach((service) => {
            weekdays.forEach((day) => {
                ['evenWeek', 'oddWeek'].forEach((weekType) => {
                    const fieldName = `urbantzmetadata.${service}.${day}.${weekType}`.toLowerCase();
                    const fieldIndex = header.findIndex((h) => h === fieldName);
                    if (fieldIndex !== -1) {
                        data.forEach((row, rowIndex) => {
                            const value = row[fieldIndex];
                            if (value && !isBoolean(value)) {
                                throw Error(
                                    `Veuillez vérifier le fichier CSV. Le champ urbantzMetadata.${service}.${day}.${weekType} doit être true/false sur la ligne ${rowIndex + 2}`,
                                );
                            }
                        });
                    }
                });
            });
        });

        // Validate each row
        data.forEach((siteArrayFromCSV: CsvDataRow, rowIndex: number): void => {
            const lineNumber = rowIndex + 2; // Adding 2 to account for 1-based index and header row

            // Type validation
            const typeIndex = header.findIndex((h) => h === 'type');
            if (typeIndex !== -1 && siteArrayFromCSV[typeIndex]) {
                if (!VALID_SITE_TYPES.includes(siteArrayFromCSV[typeIndex])) {
                    throw Error(
                        `Veuillez vérifier le fichier CSV. Type de site invalide sur la ligne ${lineNumber}. Valeurs acceptées: ${VALID_SITE_TYPES.join(', ')}`,
                    );
                }
            }

            // Time window validation
            const startTimeIndex = header.findIndex((h) => h === 'deliverytimewindow.start');
            const stopTimeIndex = header.findIndex((h) => h === 'deliverytimewindow.stop');
            if (startTimeIndex !== -1 && siteArrayFromCSV[startTimeIndex]) {
                if (!TIME_WINDOW_REGEX.test(siteArrayFromCSV[startTimeIndex])) {
                    throw Error(
                        `Veuillez vérifier le fichier CSV. Format d'heure de début invalide sur la ligne ${lineNumber}. Format attendu: HH:mm`,
                    );
                }
            }
            if (stopTimeIndex !== -1 && siteArrayFromCSV[stopTimeIndex]) {
                if (!TIME_WINDOW_REGEX.test(siteArrayFromCSV[stopTimeIndex])) {
                    throw Error(
                        `Veuillez vérifier le fichier CSV. Format d'heure de fin invalide sur la ligne ${lineNumber}. Format attendu: HH:mm`,
                    );
                }
            }

            // Date validations
            const removalDateIndex = header.findIndex((h) => h === 'removaldate');
            if (removalDateIndex !== -1 && siteArrayFromCSV[removalDateIndex]) {
                const dateValue = siteArrayFromCSV[removalDateIndex];
                if (!isValidDate(dateValue, 'YYYY-MM-DD')) {
                    throw Error(
                        `Format ou valeur de date de suppression invalide sur la ligne ${lineNumber}. Format attendu: YYYY-MM-DD (ex: 2024-03-15)`,
                    );
                }
            }

            const launchDateIndex = header.findIndex((h) => h === 'launchdate');
            if (launchDateIndex !== -1 && siteArrayFromCSV[launchDateIndex]) {
                const dateValue = siteArrayFromCSV[launchDateIndex];
                if (!isValidDate(dateValue, 'DD/MM/YYYY')) {
                    throw Error(
                        `Format ou valeur de date de lancement invalide sur la ligne ${lineNumber}. Format attendu: DD/MM/YYYY (ex: 15/03/2024)`,
                    );
                }
            }

            // Numeric fields validation
            NUMERIC_FIELDS.forEach((field) => {
                const fieldIndex = header.findIndex((h) => h === field.toLowerCase());
                if (fieldIndex !== -1 && siteArrayFromCSV[fieldIndex]) {
                    const numericValue = formatToNumber(siteArrayFromCSV[fieldIndex]);
                    if (Number.isNaN(numericValue) || numericValue < 0) {
                        throw Error(
                            `Veuillez vérifier le fichier CSV. Le champ ${field} doit être un nombre positif sur la ligne ${lineNumber}`,
                        );
                    }
                    // Vérifier si le champ doit être un entier
                    const shouldBeInteger = INTEGER_FIELDS.some(
                        (intField) =>
                            field.toLowerCase() === intField.toLowerCase() ||
                            field.toLowerCase().startsWith('reassortmentstocktargets.') ||
                            field.toLowerCase().startsWith('reassortmentonboardingstocktargets.'),
                    );

                    if (shouldBeInteger && !Number.isInteger(numericValue)) {
                        throw Error(
                            `Veuillez vérifier le fichier CSV. Le champ ${field} doit être un nombre entier sur la ligne ${lineNumber}`,
                        );
                    }
                }
            });

            // Boolean fields validation
            BOOLEAN_FIELDS.forEach((field) => {
                const fieldIndex = header.findIndex((h) => h === field.toLowerCase());
                if (fieldIndex !== -1 && siteArrayFromCSV[fieldIndex]) {
                    if (!isBoolean(siteArrayFromCSV[fieldIndex])) {
                        throw Error(
                            `Veuillez vérifier le fichier CSV. Le champ ${field} doit être true/false sur la ligne ${lineNumber}`,
                        );
                    }
                }
            });

            // Array fields validation (urbantzMetadata)
            ARRAY_FIELDS.forEach((field) => {
                const baseField = field.split('.')[1];
                WEEKDAYS.forEach((day) => {
                    WEEK_TYPES.forEach((weekType) => {
                        const fullField = `${field}.${day}.${weekType}`.toLowerCase();
                        const fieldIndex = header.findIndex((h) => h === fullField);
                        if (fieldIndex !== -1) {
                            const value = siteArrayFromCSV[fieldIndex];
                            // Check if value is empty string or valid boolean
                            if (value !== '' && !isBoolean(value)) {
                                throw Error(
                                    `Veuillez vérifier le fichier CSV. Le champ ${baseField} pour ${day} ${weekType} doit être true/false ou vide sur la ligne ${lineNumber}. Valeur trouvée: "${value}"`,
                                );
                            }
                        }
                    });
                });
            });
        });

        return { success: true };
    } catch (error) {
        return { success: false, error: getErrorAsString(error) };
    }
};
